import { Box, Button, type SelectChangeEvent, CircularProgress } from '@mui/material'
import { sendGet, sendPost } from 'hooks/use-fetch'
import { useEffect, useState } from 'react'
import { CheckList } from 'Components/CheckList'
import { DatePickerWithLabel } from 'Components/DatePicker'
import { SelectWithLabel, type SelectOption } from 'Components/SelectWithLabel'
import { DownloadFileFromPost } from 'core/Utilities'
import { toast } from 'react-toastify'
import { generatePath } from 'react-router'
import * as AppRoutes from 'Pages/Routes'
import { type msdsPeriod } from 'Pages/CaregiverPortal/types'
import { useAccountId } from 'ContextProviders/CurrentAccount'
import { AccountType, type ActiveAccount } from 'core/types'
import { ReportHeader } from 'Pages/Reports/ReportHeader'

interface childValidationItem {
  childID: number
  enrollmentID: number
  name: string
  issues: string[]
}

interface programSummary {
  isdProgramID: number
  programName: string
  isdServiceProviderID: number
  serviceProviderName: string
}

interface msdsValidation {
  childValidationItems: childValidationItem[]
  programsMissingCodes: programSummary[]
}

export function MSDSExportOptions (): JSX.Element {
  const [periods, setPeriods] = useState<msdsPeriod[]>([])
  const [startDate, setStartDate] = useState<Date | null>(null)
  const [endDate, setEndDate] = useState<Date | null>(null)
  const [programs, setPrograms] = useState<Array<SelectOption<number>>>([])
  const [selectedPeriod, setSelectedPeriod] = useState<number | null>(null)
  const [selectedPrograms, setSelectedPrograms] = useState<Array<SelectOption<number>>>([])
  const [serviceProviders, setServiceProviders] = useState<Array<SelectOption<number>>>([])
  const [selectedServiceProviders, setSelectedServiceProviders] = useState<Array<SelectOption<number>>>([])
  const [validationErrors, setValidationErrors] = useState<msdsValidation | null>(null)
  const [processing, setProcessing] = useState<boolean>(false)
  const accountId: ActiveAccount | undefined = useAccountId()
  const isdUser: boolean = (accountId !== undefined && accountId.type === AccountType.ISD)

  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      const { response } = await sendGet('/Reporting/ISDPrograms?withStateCodeOnly=true', {})
      setPrograms(response)
      if (isdUser) {
        const { response: response2 } = await sendGet('/Reporting/ISDServiceProviders', {})
        setServiceProviders(response2)
      }
      const { response: response3 } = await sendGet('/Reporting/MSDSPeriods', {})
      setPeriods(response3)
    }

    void fetchData()
  }, [])

  const periodChange = (event: SelectChangeEvent<number | null>): void => {
    const periodId: number = parseInt(String(event.target.value?.toString()))
    const period = periods.find(p => p.periodID === periodId)
    if (period !== undefined) {
      setStartDate(period.startDate)
      setEndDate(period.endDate)
    }
    setSelectedPeriod(periodId)
  }

  const validateOptions = (): boolean => {
    if (selectedServiceProviders.length === 0 && isdUser) {
      toast.error('Please select at least one Service Provider')
    } else if (selectedPrograms.length === 0) {
      toast.error('Please select at least one Program')
    } else if (selectedPeriod === null) {
      toast.error('Please select a Period')
    } else if (startDate === null) {
      toast.error('Please enter a Previous Date')
    } else if (endDate === null) {
      toast.error('Please enter a Count Date')
    } else {
      return true
    }
    return false
  }

  const dataToSubmit = (): any => {
    let isdServiceProviderIDs: number[] = []
    if (isdUser) {
      isdServiceProviderIDs = selectedServiceProviders.map(s => s.id)
    }
    const ISDProgramIDs = selectedPrograms.map(s => s.id)
    const PeriodID = selectedPeriod
    const postData = { PeriodID, startDate, endDate, isdServiceProviderIDs, ISDProgramIDs }
    return postData
  }

  const exportXML = async (): Promise<void> => {
    setValidationErrors(null)

    if (validateOptions()) {
      setProcessing(true)
      const postData = dataToSubmit()
      const { response, success } = await sendPost('/Reporting/ValidateMSDSEnrollmentXML', postData)
      if (!success) {
        alert('The provided options are not valid.')
      } else {
        const validation: msdsValidation = response
        if (validation.childValidationItems.length !== 0 || validation.programsMissingCodes.length !== 0) {
          setValidationErrors(validation)
          window.scrollTo({ top: 0, behavior: 'smooth' })
        }
        await DownloadFileFromPost('/Reporting/DownloadXMLExport', postData, 'MSDSExport.xml')
      }
      setProcessing(false)
    }
  }

  const exportWaitlistXML = async (): Promise<void> => {
    setValidationErrors(null)

    if (validateOptions()) {
      setProcessing(true)
      await DownloadFileFromPost('/Reporting/DownloadWaitlistXMLExport', dataToSubmit(), 'MSDSWaitlistExport.xml')
      setProcessing(false)
    }
  }

  const launchExportReport = async (waitlist: boolean): Promise<void> => {
    if (validateOptions()) {
      const postData = dataToSubmit()
      postData.waitlist = waitlist
      const { response, success } = await sendPost('/Reporting/MSDSExportReportURL', postData)
      if (success) {
        window.open(response, '_blank')?.focus()
      }
    }
  }

  return <>
    <ReportHeader title="MSDS Export" />

    {validationErrors !== null &&
      <>
        {validationErrors.childValidationItems.length !== 0 &&
          <>
            <div>The following {validationErrors.childValidationItems.length === 1 ? 'child has' : 'children have'} data issues that need to be fixed before they can be included in the export file:</div>
            <ul>
              {validationErrors.childValidationItems.map((item, index) => {
                return <li key={index}>
                  <div>
                    <a href={generatePath(AppRoutes.CARENETWORK_MANAGEMENT_DETAILS, { id: item.childID.toString() })} target='_blank'>{item.name}</a>
                  </div>
                  <ul>
                    {item.issues.map((issue, index2) => {
                      return <li key={index2}>{issue}</li>
                    })}
                  </ul>
                </li>
              })}
            </ul>
          </>
        }
        {validationErrors.programsMissingCodes.length !== 0 &&
          <>
            <div>The following programs need to have Fiscal Entity Codes entered for them</div>
            <table>
              <tr>
                <th>Service Provider</th>
                <th>Program</th>
              </tr>
              {validationErrors.programsMissingCodes.map((item, index) => {
                return <tr key={index}>
                  <td>{item.serviceProviderName}</td>
                  <td>{item.programName}</td>
                </tr>
              })}
            </table>
          </>
        }
      </>
    }
    <Box className='mt-10'>
      <SelectWithLabel
        width="250px"
        aria-labelledby="periodID"
        name="periodID"
        label="Period"
        required={true}
        options={periods.map((val, index) => {
          const item: SelectOption<number> = {
            id: val.periodID,
            name: val.periodName
          }
          return item
        })
        }
        value={selectedPeriod ?? undefined}
        onChange={periodChange}
      />

      <DatePickerWithLabel
        required={true}
        label='Count Date'
        name='endDate'
        onChange={setEndDate}
        value={endDate}
      />
      <DatePickerWithLabel
        required={true}
        label='Previous Date'
        name='startDate'
        onChange={setStartDate}
        value={startDate}
      />

      <CheckList label='Programs *' items={programs} selectedItems={selectedPrograms} setSelectedItems={setSelectedPrograms} allOption={true} />

      {isdUser && <CheckList label='Service Providers *' items={serviceProviders} selectedItems={selectedServiceProviders} setSelectedItems={setSelectedServiceProviders} allOption={true} />}
    </Box>

    <Box className='mt-10'>
      {processing && <CircularProgress />}
      {!processing && <>
        <Button color='secondary' variant='contained' onClick={exportXML}>XML Export</Button>
        <Button color='secondary' variant='contained' onClick={exportWaitlistXML} className='ml-10'>Waitlist XML Export</Button>
        <Button color='secondary' variant='contained' onClick={async (): Promise<void> => { await launchExportReport(false) }} className='ml-10'>View Report Data</Button>
        <Button color='secondary' variant='contained' onClick={async (): Promise<void> => { await launchExportReport(true) }} className='ml-10'>View Waitlist Report Data</Button>
      </>
      }

    </Box>
  </>
}
